import React from 'react';
import { Switch, Route, Redirect } from 'react-router-dom';

import {
  Login,
  Register,
  RegisterConfirmation,
  RequestPassword,
  ResetPassword,
  RequestPasswordConfirmation,
  ResetPasswordConfirmation,
  SelectAccount,
} from './auth/screens';
import {
  Projects,
  ProjectDetails,
  ProjectHub,
  ProjectRating,
  ProjectResults,
  Benchmark,
  ModelDetails,
  Surveys,
  CreateSurvey,
  CreatedSurvey,
  SurveyRating,
  CompletedSurvey,
  SurveyDetails,
  SurveyResults,
} from './projects/screens';
import { NewModel, ModelResults } from './models/screens';
import { PrivateRoute, LogoutRoute } from './shared/components';
import { BuildingTypeProvider } from './assess/BuildingTypeContext';

// Brain health screens loaded lazily to prevent import crashes from taking down the whole app
const BrainHealthDashboard = React.lazy(() => import('./brainHealth/screens/BrainHealthDashboard'));
const SpaceList = React.lazy(() => import('./brainHealth/screens/SpaceList'));
const SpaceEditor = React.lazy(() => import('./brainHealth/screens/SpaceEditor'));
const BhpsScorecard = React.lazy(() => import('./brainHealth/screens/BhpsScorecard'));
const Neuroscience = React.lazy(() => import('./brainHealth/screens/Neuroscience'));
const PopulationEditor = React.lazy(() => import('./brainHealth/screens/PopulationEditor'));
const Research = React.lazy(() => import('./brainHealth/screens/Research'));
const BhSpaceOptimizer = React.lazy(() => import('./brainHealth/screens/SpaceOptimizer'));
const BhFloorplan = React.lazy(() => import('./brainHealth/screens/Floorplan'));

// Healthcare facility screens (same lazy-load pattern)
const HcDashboard = React.lazy(() => import('./healthcareFacility/screens/HealthcareDashboard'));
const HcSpaceList = React.lazy(() => import('./healthcareFacility/screens/SpaceList'));
const HcSpaceEditor = React.lazy(() => import('./healthcareFacility/screens/SpaceEditor'));
const HcHdpsScorecard = React.lazy(() => import('./healthcareFacility/screens/HdpsScorecard'));
const HcNeuroscience = React.lazy(() => import('./healthcareFacility/screens/Neuroscience'));
const HcPopulationEditor = React.lazy(() => import('./healthcareFacility/screens/PopulationEditor'));
const HcSpaceOptimizer = React.lazy(() => import('./healthcareFacility/screens/SpaceOptimizer'));

// Cities & Communities screens
const CcDashboard = React.lazy(() => import('./citiesCommunities/screens/UrbanDashboard'));
const CcZoneEditor = React.lazy(() => import('./citiesCommunities/screens/ZoneEditor'));
const CcScorecard = React.lazy(() => import('./citiesCommunities/screens/UbhpsScorecard'));
const CcNeuroscience = React.lazy(() => import('./citiesCommunities/screens/Neuroscience'));
const CcImpactModel = React.lazy(() => import('./citiesCommunities/screens/ImpactModel'));
const CcResearch = React.lazy(() => import('./citiesCommunities/screens/Research'));

// Higher Education screens (NUHS + Rollins + Wake Forest framework)
const HigherEdDashboard = React.lazy(() => import('./higherEd/screens/HigherEdDashboard'));
const HigherEdProjectInfo = React.lazy(() => import('./higherEd/screens/HigherEdProjectInfo'));
const HigherEdPeople = React.lazy(() => import('./higherEd/screens/HigherEdPeople'));
const HigherEdRating = React.lazy(() => import('./higherEd/screens/HigherEdRating'));
const HigherEdFacultyOffice = React.lazy(() => import('./higherEd/screens/HigherEdFacultyOffice'));
const HigherEdModelDetails = React.lazy(() => import('./higherEd/screens/HigherEdModelDetails'));
const HigherEdSurveys = React.lazy(() => import('./higherEd/screens/HigherEdSurveys'));
const CreateHigherEdSurvey = React.lazy(() => import('./higherEd/screens/CreateHigherEdSurvey'));
const CreatedHigherEdSurvey = React.lazy(() => import('./higherEd/screens/CreatedHigherEdSurvey'));
const HigherEdSurveyRating = React.lazy(() => import('./higherEd/screens/HigherEdSurveyRating'));
const HigherEdSurveyDetails = React.lazy(() => import('./higherEd/screens/HigherEdSurveyDetails'));
const CompletedHigherEdSurvey = React.lazy(() => import('./higherEd/screens/CompletedHigherEdSurvey'));

// Unified assessment screens (config-driven)
const AssessWrapper = React.lazy(() => import('./assess/screens/AssessWrapper'));
const UnifiedDashboard = React.lazy(() => import('./assess/screens/Dashboard'));
const UnifiedSpaceEditor = React.lazy(() => import('./assess/screens/SpaceEditor'));
const UnifiedScorecard = React.lazy(() => import('./assess/screens/PointsScorecard'));
const UnifiedNeuroscience = React.lazy(() => import('./assess/screens/Neuroscience'));
const UnifiedFinancial = React.lazy(() => import('./assess/screens/FinancialModel'));
const UnifiedOptimizer = React.lazy(() => import('./assess/screens/SpaceOptimizer'));
const UnifiedSpaceList = React.lazy(() => import('./assess/screens/SpaceList'));
const UnifiedSenSEI = React.lazy(() => import('./assess/screens/SenSEI'));

const routes = (
  <Switch>
    <LogoutRoute exact path="/logout" />
    <PrivateRoute exact path="/projects">
      <Projects />
    </PrivateRoute>
    <PrivateRoute path="/projects/new">
      <ProjectDetails />
    </PrivateRoute>
    <PrivateRoute exact path="/projects/:id/hub">
      <ProjectHub />
    </PrivateRoute>
    <PrivateRoute exact path="/projects/:id/details">
      <ProjectDetails />
    </PrivateRoute>
    <PrivateRoute path="/projects/:id/rating">
      <ProjectRating />
    </PrivateRoute>
    <PrivateRoute path="/projects/:id/results">
      <ProjectResults />
    </PrivateRoute>
    <PrivateRoute exact path="/projects/:id/surveys">
      <Surveys />
    </PrivateRoute>
    <PrivateRoute exact path="/projects/:id/surveys/new">
      <CreateSurvey />
    </PrivateRoute>
    <Route exact path="/projects/:id/surveys/:surveyId">
      <SurveyRating />
    </Route>
    <PrivateRoute exact path="/projects/:id/surveys/:surveyId/created">
      <CreatedSurvey />
    </PrivateRoute>
    <Route exact path="/projects/:id/surveys/:surveyId/completed">
      <CompletedSurvey />
    </Route>
    <Route exact path="/projects/:id/surveys/:surveyId/details">
      <SurveyDetails />
    </Route>
    <PrivateRoute exact path="/projects/:id/surveys/:surveyId/results">
      <SurveyResults />
    </PrivateRoute>
    <PrivateRoute path="/projects/:id/benchmark">
      <Benchmark />
    </PrivateRoute>
    <PrivateRoute path="/projects/:id/model-details">
      <ModelDetails />
    </PrivateRoute>
    <PrivateRoute exact path="/projects/:id/floorplan">
      <React.Suspense fallback={<div style={{ color: '#fff', padding: 40 }}>Loading...</div>}>
        <BhFloorplan />
      </React.Suspense>
    </PrivateRoute>
    <PrivateRoute exact path="/projects/:id/brain-health">
      <React.Suspense fallback={<div style={{ color: '#fff', padding: 40 }}>Loading...</div>}>
        <BrainHealthDashboard />
      </React.Suspense>
    </PrivateRoute>
    <PrivateRoute exact path="/projects/:id/brain-health/spaces">
      <React.Suspense fallback={<div style={{ color: '#fff', padding: 40 }}>Loading...</div>}>
        <SpaceList />
      </React.Suspense>
    </PrivateRoute>
    <PrivateRoute exact path="/projects/:id/brain-health/spaces/:spaceId">
      <React.Suspense fallback={<div style={{ color: '#fff', padding: 40 }}>Loading...</div>}>
        <SpaceEditor />
      </React.Suspense>
    </PrivateRoute>
    <PrivateRoute exact path="/projects/:id/brain-health/scorecard">
      <React.Suspense fallback={<div style={{ color: '#fff', padding: 40 }}>Loading...</div>}>
        <BhpsScorecard />
      </React.Suspense>
    </PrivateRoute>
    <PrivateRoute exact path="/projects/:id/brain-health/neuroscience">
      <React.Suspense fallback={<div style={{ color: '#fff', padding: 40 }}>Loading...</div>}>
        <Neuroscience />
      </React.Suspense>
    </PrivateRoute>
    <PrivateRoute exact path="/projects/:id/brain-health/population">
      <React.Suspense fallback={<div style={{ color: '#fff', padding: 40 }}>Loading...</div>}>
        <PopulationEditor />
      </React.Suspense>
    </PrivateRoute>
    <PrivateRoute exact path="/projects/:id/brain-health/research">
      <React.Suspense fallback={<div style={{ color: '#fff', padding: 40 }}>Loading...</div>}>
        <Research />
      </React.Suspense>
    </PrivateRoute>
    <PrivateRoute exact path="/projects/:id/brain-health/floorplan">
      <React.Suspense fallback={<div style={{ color: '#fff', padding: 40 }}>Loading...</div>}>
        <BhFloorplan />
      </React.Suspense>
    </PrivateRoute>
    <PrivateRoute exact path="/projects/:id/brain-health/spaces/:spaceId/optimize">
      <React.Suspense fallback={<div style={{ color: '#fff', padding: 40 }}>Loading...</div>}>
        <BhSpaceOptimizer />
      </React.Suspense>
    </PrivateRoute>

    <PrivateRoute exact path="/projects/:id/higher-ed">
      <React.Suspense fallback={<div style={{ color: '#fff', padding: 40 }}>Loading...</div>}>
        <HigherEdDashboard />
      </React.Suspense>
    </PrivateRoute>
    <PrivateRoute exact path="/projects/:id/higher-ed/project-info">
      <React.Suspense fallback={<div style={{ color: '#fff', padding: 40 }}>Loading...</div>}>
        <HigherEdProjectInfo />
      </React.Suspense>
    </PrivateRoute>
    <PrivateRoute exact path="/projects/:id/higher-ed/people">
      <React.Suspense fallback={<div style={{ color: '#fff', padding: 40 }}>Loading...</div>}>
        <HigherEdPeople />
      </React.Suspense>
    </PrivateRoute>
    <PrivateRoute exact path="/projects/:id/higher-ed/rating">
      <React.Suspense fallback={<div style={{ color: '#fff', padding: 40 }}>Loading...</div>}>
        <HigherEdRating />
      </React.Suspense>
    </PrivateRoute>
    <PrivateRoute exact path="/projects/:id/higher-ed/faculty-office">
      <React.Suspense fallback={<div style={{ color: '#fff', padding: 40 }}>Loading...</div>}>
        <HigherEdFacultyOffice />
      </React.Suspense>
    </PrivateRoute>
    <PrivateRoute exact path="/projects/:id/higher-ed/model-details">
      <React.Suspense fallback={<div style={{ color: '#fff', padding: 40 }}>Loading...</div>}>
        <HigherEdModelDetails />
      </React.Suspense>
    </PrivateRoute>
    <PrivateRoute exact path="/projects/:id/higher-ed/surveys">
      <React.Suspense fallback={<div style={{ color: '#fff', padding: 40 }}>Loading...</div>}>
        <HigherEdSurveys />
      </React.Suspense>
    </PrivateRoute>
    <PrivateRoute exact path="/projects/:id/higher-ed/surveys/new">
      <React.Suspense fallback={<div style={{ color: '#fff', padding: 40 }}>Loading...</div>}>
        <CreateHigherEdSurvey />
      </React.Suspense>
    </PrivateRoute>
    <PrivateRoute exact path="/projects/:id/higher-ed/surveys/:surveyId/created">
      <React.Suspense fallback={<div style={{ color: '#fff', padding: 40 }}>Loading...</div>}>
        <CreatedHigherEdSurvey />
      </React.Suspense>
    </PrivateRoute>
    <PrivateRoute exact path="/projects/:id/higher-ed/surveys/:surveyId/details">
      <React.Suspense fallback={<div style={{ color: '#fff', padding: 40 }}>Loading...</div>}>
        <HigherEdSurveyDetails />
      </React.Suspense>
    </PrivateRoute>
    <Route exact path="/he-survey/:surveyId">
      <React.Suspense fallback={<div style={{ color: '#fff', padding: 40 }}>Loading...</div>}>
        <HigherEdSurveyRating />
      </React.Suspense>
    </Route>
    <Route exact path="/he-survey/:surveyId/completed">
      <React.Suspense fallback={<div style={{ color: '#fff', padding: 40 }}>Loading...</div>}>
        <CompletedHigherEdSurvey />
      </React.Suspense>
    </Route>

    <PrivateRoute exact path="/projects/:id/healthcare">
      <React.Suspense fallback={<div style={{ color: '#fff', padding: 40 }}>Loading...</div>}>
        <HcDashboard />
      </React.Suspense>
    </PrivateRoute>
    <PrivateRoute exact path="/projects/:id/healthcare/spaces">
      <React.Suspense fallback={<div style={{ color: '#fff', padding: 40 }}>Loading...</div>}>
        <HcSpaceList />
      </React.Suspense>
    </PrivateRoute>
    <PrivateRoute exact path="/projects/:id/healthcare/spaces/:spaceId">
      <React.Suspense fallback={<div style={{ color: '#fff', padding: 40 }}>Loading...</div>}>
        <HcSpaceEditor />
      </React.Suspense>
    </PrivateRoute>
    <PrivateRoute exact path="/projects/:id/healthcare/scorecard">
      <React.Suspense fallback={<div style={{ color: '#fff', padding: 40 }}>Loading...</div>}>
        <HcHdpsScorecard />
      </React.Suspense>
    </PrivateRoute>
    <PrivateRoute exact path="/projects/:id/healthcare/neuroscience">
      <React.Suspense fallback={<div style={{ color: '#fff', padding: 40 }}>Loading...</div>}>
        <HcNeuroscience />
      </React.Suspense>
    </PrivateRoute>
    <PrivateRoute exact path="/projects/:id/healthcare/spaces/:spaceId/optimize">
      <React.Suspense fallback={<div style={{ color: '#fff', padding: 40 }}>Loading...</div>}>
        <HcSpaceOptimizer />
      </React.Suspense>
    </PrivateRoute>
    <PrivateRoute exact path="/projects/:id/healthcare/population">
      <React.Suspense fallback={<div style={{ color: '#fff', padding: 40 }}>Loading...</div>}>
        <HcPopulationEditor />
      </React.Suspense>
    </PrivateRoute>
    <PrivateRoute exact path="/projects/:id/healthcare/floorplan">
      <React.Suspense fallback={<div style={{ color: '#fff', padding: 40 }}>Loading...</div>}>
        <BhFloorplan />
      </React.Suspense>
    </PrivateRoute>
    <PrivateRoute exact path="/projects/:id/healthcare/research">
      <React.Suspense fallback={<div style={{ color: '#fff', padding: 40 }}>Loading...</div>}>
        <Research />
      </React.Suspense>
    </PrivateRoute>

    <PrivateRoute exact path="/projects/:id/cities">
      <React.Suspense fallback={<div style={{ color: '#fff', padding: 40 }}>Loading...</div>}>
        <CcDashboard />
      </React.Suspense>
    </PrivateRoute>
    <PrivateRoute exact path="/projects/:id/cities/zones/:zoneId">
      <React.Suspense fallback={<div style={{ color: '#fff', padding: 40 }}>Loading...</div>}>
        <CcZoneEditor />
      </React.Suspense>
    </PrivateRoute>
    <PrivateRoute exact path="/projects/:id/cities/scorecard">
      <React.Suspense fallback={<div style={{ color: '#fff', padding: 40 }}>Loading...</div>}>
        <CcScorecard />
      </React.Suspense>
    </PrivateRoute>
    <PrivateRoute exact path="/projects/:id/cities/neuroscience">
      <React.Suspense fallback={<div style={{ color: '#fff', padding: 40 }}>Loading...</div>}>
        <CcNeuroscience />
      </React.Suspense>
    </PrivateRoute>
    <PrivateRoute exact path="/projects/:id/cities/impact">
      <React.Suspense fallback={<div style={{ color: '#fff', padding: 40 }}>Loading...</div>}>
        <CcImpactModel />
      </React.Suspense>
    </PrivateRoute>
    <PrivateRoute exact path="/projects/:id/cities/zones/:zoneId/optimize">
      <React.Suspense fallback={<div style={{ color: '#fff', padding: 40 }}>Loading...</div>}>
        <BuildingTypeProvider type="urban">
          <UnifiedOptimizer />
        </BuildingTypeProvider>
      </React.Suspense>
    </PrivateRoute>
    <PrivateRoute exact path="/projects/:id/cities/research">
      <React.Suspense fallback={<div style={{ color: '#fff', padding: 40 }}>Loading...</div>}>
        <CcResearch />
      </React.Suspense>
    </PrivateRoute>

    {/* Unified assessment screens (config-driven) */}
    <PrivateRoute exact path="/projects/:id/assess">
      <React.Suspense fallback={<div style={{ color: '#fff', padding: 40 }}>Loading...</div>}>
        <AssessWrapper>
          <UnifiedDashboard />
        </AssessWrapper>
      </React.Suspense>
    </PrivateRoute>
    <PrivateRoute exact path="/projects/:id/assess/spaces">
      <React.Suspense fallback={<div style={{ color: '#fff', padding: 40 }}>Loading...</div>}>
        <AssessWrapper>
          <UnifiedSpaceList />
        </AssessWrapper>
      </React.Suspense>
    </PrivateRoute>
    <PrivateRoute exact path="/projects/:id/assess/zones">
      <React.Suspense fallback={<div style={{ color: '#fff', padding: 40 }}>Loading...</div>}>
        <AssessWrapper>
          <UnifiedSpaceList />
        </AssessWrapper>
      </React.Suspense>
    </PrivateRoute>
    <PrivateRoute exact path="/projects/:id/assess/spaces/:spaceId">
      <React.Suspense fallback={<div style={{ color: '#fff', padding: 40 }}>Loading...</div>}>
        <AssessWrapper>
          <UnifiedSpaceEditor />
        </AssessWrapper>
      </React.Suspense>
    </PrivateRoute>
    <PrivateRoute exact path="/projects/:id/assess/zones/:zoneId">
      <React.Suspense fallback={<div style={{ color: '#fff', padding: 40 }}>Loading...</div>}>
        <AssessWrapper>
          <UnifiedSpaceEditor />
        </AssessWrapper>
      </React.Suspense>
    </PrivateRoute>
    <PrivateRoute exact path="/projects/:id/assess/scorecard">
      <React.Suspense fallback={<div style={{ color: '#fff', padding: 40 }}>Loading...</div>}>
        <AssessWrapper>
          <UnifiedScorecard />
        </AssessWrapper>
      </React.Suspense>
    </PrivateRoute>
    <PrivateRoute exact path="/projects/:id/assess/neuroscience">
      <React.Suspense fallback={<div style={{ color: '#fff', padding: 40 }}>Loading...</div>}>
        <AssessWrapper>
          <UnifiedNeuroscience />
        </AssessWrapper>
      </React.Suspense>
    </PrivateRoute>
    <PrivateRoute exact path="/projects/:id/assess/population">
      <React.Suspense fallback={<div style={{ color: '#fff', padding: 40 }}>Loading...</div>}>
        <AssessWrapper>
          <UnifiedFinancial />
        </AssessWrapper>
      </React.Suspense>
    </PrivateRoute>
    <PrivateRoute exact path="/projects/:id/assess/impact">
      <React.Suspense fallback={<div style={{ color: '#fff', padding: 40 }}>Loading...</div>}>
        <AssessWrapper>
          <UnifiedFinancial />
        </AssessWrapper>
      </React.Suspense>
    </PrivateRoute>
    <PrivateRoute exact path="/projects/:id/assess/zones/:zoneId/optimize">
      <React.Suspense fallback={<div style={{ color: '#fff', padding: 40 }}>Loading...</div>}>
        <AssessWrapper>
          <UnifiedOptimizer />
        </AssessWrapper>
      </React.Suspense>
    </PrivateRoute>
    <PrivateRoute exact path="/projects/:id/assess/spaces/:spaceId/optimize">
      <React.Suspense fallback={<div style={{ color: '#fff', padding: 40 }}>Loading...</div>}>
        <AssessWrapper>
          <UnifiedOptimizer />
        </AssessWrapper>
      </React.Suspense>
    </PrivateRoute>
    <PrivateRoute exact path="/projects/:id/assess/sensei">
      <React.Suspense fallback={<div style={{ color: '#fff', padding: 40 }}>Loading...</div>}>
        <AssessWrapper>
          <UnifiedSenSEI />
        </AssessWrapper>
      </React.Suspense>
    </PrivateRoute>

    <PrivateRoute path="/models/new">
      <NewModel />
    </PrivateRoute>
    <PrivateRoute path="/models/:id/results">
      <ModelResults />
    </PrivateRoute>

    <Route path="/login">
      <Login />
    </Route>
    <Route path="/register">
      <Register />
    </Route>
    <Route path="/register-confirmation">
      <RegisterConfirmation />
    </Route>
    <Route path="/request-password">
      <RequestPassword />
    </Route>
    <Route path="/request-password-confirmation">
      <RequestPasswordConfirmation />
    </Route>
    <Route path="/reset-password">
      <ResetPassword />
    </Route>
    <Route path="/reset-password-confirmation">
      <ResetPasswordConfirmation />
    </Route>
    <PrivateRoute exact path="/select-account">
      <SelectAccount />
    </PrivateRoute>
    <Redirect from="/" to="/projects" />
  </Switch>
);

export default routes;
